import { Component, ElementRef, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, ConfirmationComponent, FirebaseService, Info, InfoService, Pager, UploadTypeEnum, UtilHelper } from 'lib-trend-core';
import { BehaviorSubject, delay, Observable, Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs';

@Component({
    selector: 'info-component',
    templateUrl: './info.component.html',
    styleUrls: ['./info.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InfoComponent extends AbstractComponent implements OnInit {

  pager: Pager<Info> = new Pager<Info>({ perPage: 10 });

  listObservable: Observable<Pager<Info>>;
  private termOfSearch: Subject<string> = new Subject<string>();

  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$: Observable<boolean> = this.loadingSpinnerSubject.asObservable().pipe(delay(700));
  
  @ViewChild('fileInputLogin', { static: false }) inputFile: ElementRef;
  
  loginImage: string = null;
  file: File;
  
  private readonly LOGIN_IMAGE_FILENAME = 'login-panel-image';
  
  get loginImagePublicUrl(): string {
    const bucket = this.firebaseService['storageBucket'];
    const path = UploadTypeEnum.INFO + this.LOGIN_IMAGE_FILENAME;
    const encodedPath = encodeURIComponent(path);
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
  }

  constructor(
    public injector: Injector,
    public dialog: MatDialog,
    public infoService: InfoService,
    private firebaseService: FirebaseService
  ) {
    super(injector);
  }

  ngOnInit() {
    this.getList();
    this.loadLoginImageFromFirebase();
  }

  getList() {
    this.infoService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<Info>) => {
          this.pager = pager;
          this.setupObservableSearch();
        },
        error: (err) => this.alertService.error(err.error.message),
      });
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  delete(user: any) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (Boolean(result) === true) {
        this.infoService.delete(user._id).subscribe({
          next: (value) => {
            this.getList();
            this.alertService.success();
          },
          error: (err) => this.alertService.error(err.error.message),
        })
      }
    })
  }

  setupObservableSearch() {
    this.listObservable = this.termOfSearch
      .pipe(debounceTime(500))
      .pipe(distinctUntilChanged())
      .pipe(
        switchMap((term) => {
          return this.infoService.getAll(this.pager.page, this.pager.perPage, term, this.searchParams);
        })
      )
    this.listObservable.subscribe((pager: Pager<Info>) => {
      this.pager = pager;
    })
  }

  search(term: string) {
    this.pager.page = 1;
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  addNew() {
    this.router.navigate(['/info/add']);
  }

  addLoginImage() {
    this.inputFile.nativeElement.click();
  }

  async onSelectLoginImage(event: Event) {
    this.loadingSpinnerSubject.next(true);

    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      this.loadingSpinnerSubject.next(false);
      return;
    }

    this.file = file;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const contents = e.target?.result as ArrayBuffer;
      const base64 = UtilHelper.arrayBufferToBase64(contents);
      try {
        await this.firebaseService.uploadFile(
          this.file,
          this.LOGIN_IMAGE_FILENAME,
          UploadTypeEnum.INFO,
        );
        
        this.loginImage = this.loginImagePublicUrl;
        
        this.alertService.success('Imagem salva! URL sempre será a mesma.');
      } catch (error) {
        this.alertService.error('Falha ao enviar a imagem.');
      } finally {
        this.loadingSpinnerSubject.next(false);
        input.value = '';
      }
    };

    reader.readAsArrayBuffer(file);
  }

  removeLoginMedia(): void {
    if (this.loginImage) {
      const dialogRef = this.dialog.open(ConfirmationComponent, {
        width: '600px',
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadingSpinnerSubject.next(true);
          this.firebaseService
            .deleteFile(this.loginImage)
            .then(() => {
              this.loginImage = null;
              if (this.inputFile?.nativeElement) {
                this.inputFile.nativeElement.value = '';
              }
              this.alertService.success('Imagem removida com sucesso!');
            })
            .catch((error) => {
              this.alertService.error('Erro ao remover a imagem.');
            })
            .finally(() => {
              this.loadingSpinnerSubject.next(false);
            });
        }
      });
    }
  }

  onImageLoadError(): void {
    this.loginImage = null;
  }

  loadLoginImageFromFirebase(): void {
    const filePath = UploadTypeEnum.INFO + this.LOGIN_IMAGE_FILENAME;
    
    this.firebaseService['storage'].ref(filePath).getDownloadURL()
      .subscribe({
        next: () => {
          this.loginImage = this.loginImagePublicUrl;
        },
        error: () => {
          this.loginImage = null;
        }
      });
  }

}
